<script>
    import { toast } from "@zerodevx/svelte-toast";
    import { toastThemeError, toastThemeInfo } from "../toasts.js";
    import Preview from "./Preview.svelte";
    import { hostname, isAuthenticated, showDebug, stack } from "../stores.js";
    import { selectedPreview, finalFdkSettings, centershiftGapStack, lastCentershiftPreviewImageIndex, newlyRequestedCentershiftPreviewSlices } from "../previewstores.js";
    import RangeSlider from "svelte-range-slider-pips";
    import { formatJson, runPreviewApi, now } from "../utils.js";
    import Modal from "./Modal.svelte";
    import { pipelineObserver } from '../pipelinestores.js';
    import { tooltip } from "svooltip";
    import 'svooltip/styles.css';

    export let params;
    export let centershiftSlices;
    export let sliceIndex;
    export let centershiftSlicePreviews;

    export let hasBeamhardCuppingSlicePreviews; // Only needed to know whether we should generate BEAMHARD_CUPPING previews with new WIDE_CENTERSHIFT slices
    export let allPhaseRetrievalPreviewSettings; // Used when adding new slice, which needs to be done for all phase retrievals

    let centershiftSliderRange = undefined; // The number of parameter values for the currently selected preview
    let CENTERSHIFTSLIDERVALUE_DEFAULT = undefined; // Default value centershift is typically the middle of the range. Depends on the range of values
    let centershiftSliderValueArr = [CENTERSHIFTSLIDERVALUE_DEFAULT]; // A single slider handle
    let centerval; // The centershift display value to show below slider

    let currentPreviewOutputIndex = 0;
    let centershiftPreviewImageIndex; // = isNumeric($lastCentershiftPreviewImageIndex) ? $lastCentershiftPreviewImageIndex : CENTERSHIFTSLIDERVALUE_DEFAULT;

    $: initPreview($selectedPreview);

    function updateFdkSettings(options) {
        $finalFdkSettings = {
            ...$finalFdkSettings,
            ...options,
        };

        console.log("FINAL FDK SETTINGS ARE NOW: ", $finalFdkSettings);
    }

    function deleteFdkSetting(settingToDelete) {
        delete $finalFdkSettings?.[settingToDelete];
        $finalFdkSettings = $finalFdkSettings;
    }

    // Sets the final FDK centershift setting to the value determined by the slider
    function onChangedCenterShiftValue(previewImageIndex) {
        if (!$selectedPreview) return;

        let centershiftval = (
            $selectedPreview?.preview_output?.[currentPreviewOutputIndex]?.parameter_values?.[previewImageIndex] * 1000
        ).toFixed(3);
        console.log("UPDATED CENTERSHIFT VALUE FROM SLIDER TO: ", centershiftval);
        
        // Update the input field
        centerval = centershiftval;

        // Now update the final fdk settings with slider value
        //let changedSettings = { centershift_object_mm: Math.round(centershiftval * 1000) / 1000000 }; // Used to be rounded value
        let changedSettings = { centershift_object_mm: $selectedPreview?.preview_output?.[currentPreviewOutputIndex]?.parameter_values?.[previewImageIndex] };

        console.log("CENTERSHIFT CHANGED FDK SETTINGS TO: ", changedSettings);
        updateFdkSettings(changedSettings);
    }

    function resetAllCentershiftFields() {
        if (!$selectedPreview) return;

        currentPreviewOutputIndex = $selectedPreview?.preview_output?.findIndex(s => s.slice == sliceIndex) || 0;
        centerval = (
            $selectedPreview?.preview_output?.[currentPreviewOutputIndex]?.parameter_values?.[CENTERSHIFTSLIDERVALUE_DEFAULT] * 1000
        ).toFixed(0);
        centershiftSliderValueArr = [CENTERSHIFTSLIDERVALUE_DEFAULT];
        centershiftPreviewImageIndex = CENTERSHIFTSLIDERVALUE_DEFAULT;

        resetSlider();
    }

    let showModal = false;
    let centershiftPreviewSettings;
    let multiCentershiftPreviewSettings = {
        centershiftPreviewSettingsArr: [],
        slices: 0
    };

    const previewWideCentershift = {
        preview_type: "WIDE_CENTERSHIFT"
    };

    const previewBeamhardCupping = {
        preview_type: "BEAMHARD_CUPPING",
    };

    function startAddWideCentershiftSlice(sliceindex) {
        
        console.log(`STARTING ADD WIDE CENTERSHIFT PREVIEW FOR SLICE ${sliceIndex} AND ${allPhaseRetrievalPreviewSettings?.map(b => b.base)}`);

        centershiftPreviewSettings = undefined;
        multiCentershiftPreviewSettings = {
            centershiftPreviewSettingsArr: [],
            slices: 0
        };
        let centershiftPreviewSettingsArr = [];

        allPhaseRetrievalPreviewSettings.forEach(cps => {
            console.log('NOW ADDING PHASE RETRIEVAL', cps);

            let centershiftPreviewSettings = {
                generic_preview_input: {
                    preprocessing_settings: {
                        // Empty but needed for API syntax
                    },
                    phase_retrieval_settings: {
                        phase_retrieval: {
                            delta: cps.delta,
                            mu: cps.mu
                        }
                    },
                    reconstruction_settings: {
                        // Empty but needed for API syntax
                    },
                    slices: [sliceIndex]
                }
            };

            // If we already have BEAMHARD_CUPPING previews in the group for this centershift,
            // we should generate such preview also for this slice 
            if (hasBeamhardCuppingSlicePreviews) {
                centershiftPreviewSettings.previews = [previewWideCentershift, previewBeamhardCupping];
            } else {
                centershiftPreviewSettings.previews = [previewWideCentershift];
            }

            centershiftPreviewSettingsArr.push(centershiftPreviewSettings);
        });

        multiCentershiftPreviewSettings = {
            centershiftPreviewSettingsArr: centershiftPreviewSettingsArr,
            slices: [sliceindex]
        };

        console.log('startAddWideCentershiftSlice PREPARED THESE JSONS: ', multiCentershiftPreviewSettings);

        showModal = true;
    }

    //let newlyRequestedCentershiftPreviewSlices = new Set();

    async function onConfirmCentershiftPreviewSettings() {
        let settings = multiCentershiftPreviewSettings?.centershiftPreviewSettingsArr || centershiftPreviewSettings;

        settings?.forEach(async cps => {
            console.log("OK, CALLING PREVIEW PIPELINE FOR WIDE CENTERSHIFT...", params?.session);
            const previewInput = cps;
            const runPreviewApiResponse = await runPreviewApi(
                $hostname,
                $isAuthenticated,
                $stack,
                params.session,
                params.tomography,
                previewInput
            );
            if (isNumeric(runPreviewApiResponse)) {
                let msg = `${now()}<br/>Run preview API error: ${runPreviewApiResponse}`;
                toast.push(msg, toastThemeError);
                return;
            }

            // The API should respond with a fully qualified pipeline name
            let pipelineId = runPreviewApiResponse?.id;

            // Make a note of the slice index we requested to add, so that we have a chance 
            // to indicate to the user that it is being processed and avoid that the user
            // request the same slice again
            if (!centershiftSlices?.includes(sliceIndex)) {
                $newlyRequestedCentershiftPreviewSlices.add(sliceIndex);
                $newlyRequestedCentershiftPreviewSlices = $newlyRequestedCentershiftPreviewSlices;
            }

            // Indicate to the user that a new preview job has been started
            let msg = `${now()}<br/>`;
            msg += `Started centershift preview at slice ${cps?.generic_preview_input?.slices}`;
            const centershift = cps?.generic_preview_input?.reconstruction_settings?.centershift_object_mm;
            if (!isNaN(centershift)) {
                msg +=` with centershift ${centershift} mm`;
            }
            const tolerance = cps?.generic_preview_input?.reconstruction_settings?.centershift_tolerance_object_mm;
            if (!isNaN(tolerance)) {
                msg += ` and tolerance ${tolerance} mm`;
            }
            if (cps?.centershift_optimization) {
                msg += ` using ${cps?.centershift_optimization} optimization`;
            }
            if (pipelineId) {
                msg += ` <br/>(${pipelineId})`;
            }
            toast.push(msg, toastThemeInfo);

            // Add the new pipeline to the pipeline observables list
            $pipelineObserver.addPipelineToObserve(pipelineId);
        });
    }

    let showJson = false;
    function onShowJson() {
        showJson = true;
    }

    const REFINE_EXPAND_FACTOR = 2;
    const DEFAULT_VISIBLE_RANGE_SLIDER_VALUES = 15;
    let visibleRangeSliderValues = DEFAULT_VISIBLE_RANGE_SLIDER_VALUES;
    let shownRange = {min: 0, max: centershiftSliderRange - 1};
    let currentGapBetweenSteps = (shownRange.max - shownRange.min) / (visibleRangeSliderValues - 1);

    let shownValues = [];
    function onRefineRange() {
        // Stop showing refine range highlight
        showRefineRange = false;

        let centervalPercentageFromLeft = centershiftPreviewImageIndex / centershiftSliderRange;

        let centervalGapsFromLeft = Math.floor(centervalPercentageFromLeft * (visibleRangeSliderValues));

        let newGapBetweenSteps = Math.floor(currentGapBetweenSteps / REFINE_EXPAND_FACTOR);
        if (newGapBetweenSteps < 1) return;

        let newShownRange = {
            min: centershiftPreviewImageIndex - newGapBetweenSteps * centervalGapsFromLeft,
            max: centershiftPreviewImageIndex + newGapBetweenSteps * (visibleRangeSliderValues - 1 - centervalGapsFromLeft)
        };

        // Sanity check: If range is outside full range, abort
        if (newShownRange.min < 0) return;
        if (newShownRange.max > centershiftSliderRange - 1) return;

        // Save the last known gap value before we change it
        $centershiftGapStack.push(currentGapBetweenSteps);
        console.log('PUSHED TO GAPSTACK: ', currentGapBetweenSteps);
        $centershiftGapStack = $centershiftGapStack;

        currentGapBetweenSteps = newGapBetweenSteps;

        shownValues = [];
        for (let ix = 0; ix < visibleRangeSliderValues; ix++) {
            shownValues.push(newShownRange.min + newGapBetweenSteps * ix);
        }
        console.log("SLIDER INDICES SHOWING:", shownValues);

        shownRange = {...newShownRange};
    }

    function onExpandRange() {
        // Stop showing expand range highlight
        showExpandRange = false;

        let centervalPercentageFromLeft = centershiftPreviewImageIndex / centershiftSliderRange;

        let centervalGapsFromLeft = Math.floor(centervalPercentageFromLeft * (visibleRangeSliderValues));

        let newGapBetweenSteps = $centershiftGapStack.peek();
        if (!newGapBetweenSteps) {
            // Fallback if nothing in stack
            newGapBetweenSteps = Math.floor(currentGapBetweenSteps * REFINE_EXPAND_FACTOR);
        }
        if (newGapBetweenSteps * (visibleRangeSliderValues - 1) > centershiftSliderRange) return;

        let newShownRange = {
            min: centershiftPreviewImageIndex - newGapBetweenSteps * centervalGapsFromLeft,
            max: centershiftPreviewImageIndex + newGapBetweenSteps * (visibleRangeSliderValues - 1 - centervalGapsFromLeft)
        };

        // Sanity check: If range is outside full range, abort
        if (newShownRange.min < 0) return;
        if (newShownRange.max > centershiftSliderRange - 1) return;

        // OK, we will definitely use the new gap value, so consume it
        $centershiftGapStack.pop();
        $centershiftGapStack = $centershiftGapStack;

        currentGapBetweenSteps = newGapBetweenSteps;

        shownValues = [];
        for (let ix = 0; ix < visibleRangeSliderValues; ix++) {
            shownValues.push(newShownRange.min + newGapBetweenSteps * ix);
        }
        console.log("SLIDER INDICES SHOWING:", shownValues);

        shownRange = {...newShownRange};
    }

    let isCentervalLocked = false;
    function onToggleSelectCentershift() {
        // Lock centerval
        isCentervalLocked = !isCentervalLocked;
    }

    function isNumeric(num) {
        return (typeof num === "number" || (typeof num === "string" && num.trim() !== "")) && !isNaN(num);
    }

    function resetSlider() {

        // 1. Find out how far from the minimum (low/left) end the centerval is
        let centervalPercentageFromLeft = centershiftPreviewImageIndex / centershiftSliderRange;

        // 2. Calculate how many gaps from the left the centerval is placed (e.g. 4 gaps)
        let centervalGapsFromLeft = Math.floor(centervalPercentageFromLeft * (visibleRangeSliderValues));

        // 3a. Calculate how large the gaps can be to fit to the left of the centerval
        // Special handling for when centervalGapsFromLeft is 0, then force to gapBetweenStepsLeft to one step less than full
        // (exactly full woould mean that the high/right index would be the array length, which will cause an error)
        // UPDATE: Testing if we actually can have the full range after all
        let gapBetweenStepsLeft = Math.floor(centershiftSliderRange * centervalPercentageFromLeft / centervalGapsFromLeft);
        //if (!isNumeric(gapBetweenStepsLeft) || gapBetweenStepsLeft==Infinity) gapBetweenStepsLeft = Math.floor((centershiftSliderRange / (visibleRangeSliderValues - 1) - 1));
        if (!isNumeric(gapBetweenStepsLeft) || gapBetweenStepsLeft==Infinity) gapBetweenStepsLeft = Math.floor((centershiftSliderRange / (visibleRangeSliderValues - 1) - 0));

        
        // 3b. Calculate how large the caps can be to fit to the right of the centerval
        // Special handling for when normal value is 0 or not numeric (e.g. at the extreme right)
        //let gapBetweenStepsRight = Math.floor(centershiftSliderRange * centervalPercentageFromLeft / (visibleRangeSliderValues - 1 - centervalGapsFromLeft))
        //if (!isNumeric(gapBetweenStepsRight) || gapBetweenStepsRight == 0) gapBetweenStepsRight = Math.floor((centershiftSliderRange / (visibleRangeSliderValues - 1) - 1));
        let gapBetweenStepsRight = Math.floor((centershiftSliderRange - (centershiftPreviewImageIndex - gapBetweenStepsLeft * centervalGapsFromLeft)) / (visibleRangeSliderValues - 1));

        // 4. Pick the smallest gap that fits either side
        let gapBetweenSteps = Math.min(gapBetweenStepsLeft, gapBetweenStepsRight);

        // 5. Calculate the exact index to the left of the centerval we can start the shown steps
        shownRange = {
            min: centershiftPreviewImageIndex - gapBetweenSteps * centervalGapsFromLeft,
            max: centershiftPreviewImageIndex + gapBetweenSteps * (visibleRangeSliderValues - 1 - centervalGapsFromLeft)
        };

        $centershiftGapStack.clear();

        $centershiftGapStack = $centershiftGapStack;
        currentGapBetweenSteps = gapBetweenSteps;

        console.log("RESET SLIDER RANGE: ", shownRange);
        shownValues = [];
        for (let ix = 0; ix < visibleRangeSliderValues; ix++) {
            shownValues.push(shownRange.min + gapBetweenSteps * ix);
        }
        console.log("SLIDER INDICES SHOWING:", shownValues);

    }

    // Use current info about image index, gap stack and range to set the slider to a specific state
    function presetSlider() {
        let centervalPercentageFromLeft = centershiftPreviewImageIndex / centershiftSliderRange;
        let centervalGapsFromLeft = Math.floor(centervalPercentageFromLeft * (visibleRangeSliderValues));
        let gapBetweenSteps = Math.floor($centershiftGapStack?.peek() / REFINE_EXPAND_FACTOR);

        shownRange = {
            min: centershiftPreviewImageIndex - gapBetweenSteps * centervalGapsFromLeft,
            max: centershiftPreviewImageIndex + gapBetweenSteps * (visibleRangeSliderValues - 1 - centervalGapsFromLeft)
        };

        currentGapBetweenSteps = gapBetweenSteps;

        console.log("PRESET SLIDER RANGE: ", shownRange);
        shownValues = [];
        for (let ix = 0; ix < visibleRangeSliderValues; ix++) {
            shownValues.push(shownRange.min + gapBetweenSteps * ix);
        }
        console.log("SLIDER INDICES SHOWING:", shownValues);
    }


    // Helper function that finds the nearest selected preview image index that matches the given centershift
    function getPreviewImageIndexFromCentershiftValue(centershift_mm) {
        if (!isNumeric(centershift_mm)) return;
        //DEBUG: console.log(`@@@@@@@@@@@@@@@@@@@ MATCHING NEAREST CENTERSHIFT PREVIEW IMAGE INDEX WITH ${centershift_mm}`)
        let currentImageIndex = undefined;
        let smallestDistance = 1.1e9;
        const arr = $selectedPreview?.preview_output?.[currentPreviewOutputIndex]?.parameter_values;
        for (let i = 0; i < arr.length; i++) {
            const currentDistance = Math.abs(arr[i] - centershift_mm);
            if (currentDistance > smallestDistance) break;

            currentImageIndex = i;
            if (currentDistance < smallestDistance) smallestDistance = currentDistance;
        }
        return currentImageIndex;
    }

    // This should be called whenever the selectedPreview changes
    function initPreview($selectedPreview) {
        console.log("CENTERSHIFT SEES SLICES: ", centershiftSlices);
        // In case we have an updated list of slices (e.g. after page refresh), don't list the slice as being processed
        centershiftSlices?.map(s => {
            $newlyRequestedCentershiftPreviewSlices.delete(sliceIndex);
            $newlyRequestedCentershiftPreviewSlices = $newlyRequestedCentershiftPreviewSlices;
        });
        if ($selectedPreview) {
            console.log("++++++++ INIT CENTERSHIFTPREVIEW -- ", $selectedPreview);

            // If the current centershift image index is not set, set it to the last known if it exists, otherwise
            // set it to the index that closest matches the current user centershift setting
            if (!isNumeric(centershiftPreviewImageIndex)) {
                if (isNumeric($lastCentershiftPreviewImageIndex)) {
                    // TODO: This breaks down if the selected preview we are switching to has a different number of images
                    // than the one that where the index was set. This can happen if calibration tolerance changes.
                    // To work around this, we could either store the last centershift preview image count and make sure it matches,
                    // or we could do a sanity check that the centershift value is the same (or almost the same).
                    centershiftPreviewImageIndex = $lastCentershiftPreviewImageIndex;
                } else {
                    centershiftPreviewImageIndex = getPreviewImageIndexFromCentershiftValue($finalFdkSettings?.centershift_object_mm);
                }
            }
            console.log("USING CENTERSHIFT PREVIEW IMAGE INDEX", centershiftPreviewImageIndex);
            currentPreviewOutputIndex = $selectedPreview?.preview_output?.findIndex(s => s.slice == sliceIndex) || 0;
            console.log("SELECTED PREVIEW HAS ", $selectedPreview?.preview_output?.[currentPreviewOutputIndex]?.parameter_values?.length, " IMAGES");

            if ($selectedPreview.preview_input.preview_input.preview_type == "WIDE_CENTERSHIFT") {
                centershiftSliderRange = $selectedPreview?.preview_output?.[currentPreviewOutputIndex]?.parameter_values?.length;
                CENTERSHIFTSLIDERVALUE_DEFAULT = Math.floor((centershiftSliderRange - 1) / 2);

                // TODO: Something like this was tried to avoiding RangeSlider seeing NaN values, but unfortunately
                // it may cause current index to be non-integer, probably only when we have refined range first.
                // So we revert the change by commenting out this snippet until we have an accurate solution. 
                //shownRange = {min: 0, max: centershiftSliderRange - 1};
                //currentGapBetweenSteps = (shownRange.max - shownRange.min) / (visibleRangeSliderValues - 1);

                // Check that the centershift is at least almost the same as the value already chosen by the user. 
                // If so, we can proceed and use the centershiftPreviewImageIndex to preset the slider
                const isSameInput = Math.abs( 
                    ($selectedPreview.preview_input.generic_preview_input.reconstruction_settings.centershift_object_mm * 1000).toFixed(0) - 
                    ($finalFdkSettings?.centershift_object_mm * 1000).toFixed(0) ) < 1;

                if (isSameInput) {
                    if ($lastCentershiftPreviewImageIndex != centershiftPreviewImageIndex) {
                        $lastCentershiftPreviewImageIndex = centershiftPreviewImageIndex;
                    }
                    if (!$centershiftGapStack?.isEmpty()) {
                        presetSlider();
                    }
                    centershiftSliderValueArr = [centershiftPreviewImageIndex];
                } else {
                    if (isNumeric($finalFdkSettings?.centershift_object_mm)) {
                        centershiftPreviewImageIndex = getPreviewImageIndexFromCentershiftValue($finalFdkSettings?.centershift_object_mm);
                    } else {
                        centershiftPreviewImageIndex = CENTERSHIFTSLIDERVALUE_DEFAULT;
                    }

                    // Set the slider
                    centershiftSliderValueArr = [centershiftPreviewImageIndex];

                    // Store the new image index
                    if ($lastCentershiftPreviewImageIndex !== centershiftPreviewImageIndex) {
                        $lastCentershiftPreviewImageIndex = centershiftPreviewImageIndex;
                    }
                }

                visibleRangeSliderValues = DEFAULT_VISIBLE_RANGE_SLIDER_VALUES;
                if ($centershiftGapStack?.isEmpty()) {
                    resetSlider();
                } else if (!isNumeric(shownRange.min) || !isNumeric(shownRange.max)) {
                    // In case the slider range has not been initialised, e.g. first refining range, then
                    // switching away from wide centershift and then back again, calculate the values now
                    presetSlider();
                }
            }

            onChangedCenterShiftValue(centershiftPreviewImageIndex);
        }
    }

    function formatShownValues(shownValues, centershiftPreviewImageIndex) {
        const shownIndexStringArray = shownValues.map(v => {return v==centershiftPreviewImageIndex ? `<b>${v}</b>` : `${v}`});
        let joinedShownIndexStringArray = shownIndexStringArray.join(',');
        return joinedShownIndexStringArray;
    }

    function formatGapStack(gapStack) {
        return `[${gapStack.size()}]: ${gapStack.toArray()?.join(',')}`;
    }

    //////// BEGIN Refine range highlight
    function calculateRefinedRangePercentages() {
        let centervalPercentageFromLeft = centershiftPreviewImageIndex / centershiftSliderRange;

        let centervalGapsFromLeft = Math.floor(centervalPercentageFromLeft * (visibleRangeSliderValues));

        let newGapBetweenSteps = Math.floor(currentGapBetweenSteps / REFINE_EXPAND_FACTOR);
        if (newGapBetweenSteps < 1) return;

        let newShownRangePercentage = {
            min: (centershiftPreviewImageIndex - newGapBetweenSteps * centervalGapsFromLeft - shownRange.min) / (shownRange.max - shownRange.min),
            max: (centershiftPreviewImageIndex + newGapBetweenSteps * (visibleRangeSliderValues - 1 - centervalGapsFromLeft) - shownRange.min) / (shownRange.max - shownRange.min)
        };

        return newShownRangePercentage;
    }
    let showRefineRange = false;
    let sliderComponent;
    $: showRefineSlider(sliderComponent, showRefineRange);

    function showRefineSlider(slider, showRefineRange) {
        if (slider) {
            if (!showRefineRange) {
                slider.style="--rangeleft: 0%; --rangeright: 100%; --refine-color-edge: var(--slider); --refine-color-mid: var(--slider);";
            } else {
                // Calculate left and right ranges as percentage of the entire currently shown bar
                let pc = calculateRefinedRangePercentages();

                let rangePercentageLeft = pc?.min || 0;
                let rangePercentageRight = pc?.max || 1;

                // Show a highlighted section of the range bar to indicate next refine (zoomed-in) level
                slider.style=`--rangeleft: ${rangePercentageLeft * 100}%; --rangeright: ${rangePercentageRight * 100}%; --refine-color-edge: var(--slider); --refine-color-mid: hsl(219, 73%, 65%);`;
            }
        }
    }
    //////// END Refine range highlight

    //////// BEGIN Expand range highlight
    function calculateExpandedRangePercentages() {
        let centervalPercentageFromLeft = centershiftPreviewImageIndex / centershiftSliderRange;

        let centervalGapsFromLeft = Math.floor(centervalPercentageFromLeft * (visibleRangeSliderValues));

        let newGapBetweenSteps = $centershiftGapStack.peek();
        if (!newGapBetweenSteps) {
            // Fallback if nothing in stack
            newGapBetweenSteps = Math.floor(currentGapBetweenSteps * REFINE_EXPAND_FACTOR);
        }
        if (newGapBetweenSteps * (visibleRangeSliderValues - 1) > centershiftSliderRange) return;

        let newShownRange = {
            min: centershiftPreviewImageIndex - newGapBetweenSteps * centervalGapsFromLeft,
            max: centershiftPreviewImageIndex + newGapBetweenSteps * (visibleRangeSliderValues - 1 - centervalGapsFromLeft)
        };

        let newShownRangePercentages = {
            min: (shownRange.min - newShownRange.min) / (newShownRange.max - newShownRange.min),
            max: (shownRange.max - newShownRange.min) / (newShownRange.max - newShownRange.min)
        };

        return newShownRangePercentages;
    }
    let showExpandRange = false;
    $: showExpandSlider(sliderComponent, showExpandRange);

    function showExpandSlider(slider, showExpandRange) {
        if (slider) {
            if (!showExpandRange) {
                slider.style="--rangeleft: 0%; --rangeright: 100%; --pipsleft: 0%; --pipsright: 100%; --refine-color-edge: var(--slider); --refine-color-mid: var(--slider);";
            } else {
                // Calculate left and right ranges as percentage of the entire currently shown bar
                let pc = calculateExpandedRangePercentages();
                let rangePercentageLeft = pc?.min || 0;
                let rangePercentageRight = pc?.max || 1;

                // Show highlighted sections of the range bar to indicate next expand (zoomed-out) level
                slider.style.setProperty('--rangeleft', `${rangePercentageLeft * 100}%`);
                slider.style.setProperty('--rangeright', `${rangePercentageRight * 100}%`);
                slider.style.setProperty('--pipsleft', `${rangePercentageLeft * 100}%`);
                slider.style.setProperty('--pipsright', `${rangePercentageRight * 100}%`);
                slider.style.setProperty('--refine-color-edge', `hsl(219, 73%, 65%)`);
                slider.style.setProperty('--refine-color-mid', `var(--slider)`);
            }
        }
    }
    //////// END Expand range highlight
</script>

<Preview
    headerText="Centershift preview"
    type="CENTERSHIFT"
    {centershiftSlices}
    {sliceIndex}
    availableSlicePreviews={centershiftSlicePreviews}
    previewImageIndex={centershiftPreviewImageIndex}
    on:carousel>
    {#if $showDebug}
        <annotation style="margin-left: 0; font-family: monospace; display: block; line-break: anywhere; max-width: 768px;">
            shown index range={shownRange.min}..{shownRange.max} &nbsp; current index={centershiftPreviewImageIndex} (originally {centershiftSliderValueArr}) &nbsp; visible steps={visibleRangeSliderValues} &nbsp; current step size={currentGapBetweenSteps}
            <br />
            shown indices={@html formatShownValues(shownValues, centershiftPreviewImageIndex)}
            <br />
            gap stack={formatGapStack($centershiftGapStack)} &nbsp; #spans={centershiftSliderRange / 64}
            {#if centershiftSliderRange / 64 > 4}
                👀
            {/if}
            (total range 0..{centershiftSliderRange} / {$selectedPreview?.preview_output?.[currentPreviewOutputIndex]?.parameter_values[0]}..{$selectedPreview?.preview_output?.[currentPreviewOutputIndex]?.parameter_values[centershiftSliderRange-1]})
            <br />
            {#if $selectedPreview}
                SLICES: {$selectedPreview.preview_input.generic_preview_input.slices}<br />&nbsp;
            {:else}
                NO SELECTED PREVIEW FOR {sliceIndex}<br />&nbsp;
            {/if}
        </annotation>
    {/if}
    {#if !$selectedPreview?.preview_output?.find((s) => s.slice === sliceIndex)?.slice}
        <h3>No previews available</h3>
        {#if !$newlyRequestedCentershiftPreviewSlices.has(sliceIndex)}
            <p>Please go ahead and generate a preview...</p>
        {:else}
            <p>Slice {sliceIndex} is currently processing. Please wait...</p>
        {/if}
    {:else if isNumeric(shownRange.min) && isNumeric(shownRange.max)}
        <RangeSlider
            id="previewsliderCentershift"
            bind:slider={sliderComponent}
            disabled={isCentervalLocked}
            values={centershiftSliderValueArr}
            min={shownRange.min}
            max={shownRange.max}
            step={currentGapBetweenSteps}
            pips
            float
            first="label"
            last="label"
            suffix="μm"
            formatter={(v) => ($selectedPreview?.preview_output?.[currentPreviewOutputIndex]?.parameter_values?.[v] * 1000).toFixed(0)}
            handleFormatter={(v) => {
                // DEBUG: console.log('SLIDER HANDLE INDEX IS NOW: ', v, ' AND FLOOR WOULD BE: ', Math.floor(v));
                // DEBUG: console.log(`SLIDER RANGE=${shownRange.min}..${shownRange.max}, GAPS=${currentGapBetweenSteps}, CURRENT INDEX=${centershiftPreviewImageIndex}`);
                return ($selectedPreview?.preview_output?.[currentPreviewOutputIndex]?.parameter_values?.[v] * 1000).toFixed(3);
            }}
            springValues={{stiffness: 1, damping:1}}
            on:change={(e) => {
                centershiftPreviewImageIndex = Math.floor(e.detail.value);
                console.log("SLIDER=", e.detail.value, ", => INDEX=", centershiftPreviewImageIndex);
                $lastCentershiftPreviewImageIndex = centershiftPreviewImageIndex;
                onChangedCenterShiftValue(centershiftPreviewImageIndex);
            }}
            style="--rangeleft: {50% - 1 / REFINE_EXPAND_FACTOR / 2}; --rangeright: {50% + 1 / REFINE_EXPAND_FACTOR / 2};" />
    {/if}
    <centershift-advanced>
        <form class="centershift-form">
            {#if $selectedPreview}
                <!-- svelte-ignore a11y-label-has-associated-control -->
                <label><span use:tooltip={{content: "Middle value for the range", placement: "top"}}>Center</span></label>
                {centerval} μm
                <span></span>
                <button type="button" on:click={resetAllCentershiftFields}>Reset</button>
                <span></span>
                <span></span><span></span><span></span>
                <button type="button" class={(currentGapBetweenSteps <= 1) ? "disabled" : ""} on:mouseenter={() => {showRefineRange=true}} on:mouseleave={() => {showRefineRange=false}} on:click={onRefineRange}>Refine</button>
                <button type="button" class={$centershiftGapStack?.isEmpty() ? "disabled" : ""} on:mouseenter={() => {if (!$centershiftGapStack?.isEmpty()) showExpandRange=true;}} on:mouseleave={() => {showExpandRange=false}} on:click={onExpandRange} >Expand</button>
            {:else}
                {#if !$newlyRequestedCentershiftPreviewSlices.has(sliceIndex)}
                    <button type="button"
                        style="grid-column: 2 / span 3"
                        on:click={() => {
                            startAddWideCentershiftSlice(sliceIndex);
                        }}>Add preview for slice {sliceIndex}...
                    </button>
                {/if}
            {/if}
        </form>
    </centershift-advanced>
    {#if showModal}
        <Modal
            on:close={() => {
                showJson = false;
                showModal = false;
            }}
            on:ok={() => {
                showModal = false;
                onConfirmCentershiftPreviewSettings();
                showJson = false;
            }}
            on:json={onShowJson}
            jsonable={true}>
            <h2 slot="header">Confirm centershift preview settings</h2>
            {#if showJson}
                {#if multiCentershiftPreviewSettings.centershiftPreviewSettingsArr.length > 0}
                    {#each multiCentershiftPreviewSettings.centershiftPreviewSettingsArr as settings}
                        <pre>{formatJson(settings)}</pre>
                    {/each}
                {:else}
                    <pre>{formatJson(centershiftPreviewSettings)}</pre>
                {/if}
            {:else}
                <h3>Preprocessing settings</h3>
                <settings>
                    {#if centershiftPreviewSettings?.generic_preview_input?.preprocessing_settings?.normalization?.do}
                    normalization: {centershiftPreviewSettings?.generic_preview_input?.preprocessing_settings
                        ?.normalization?.do}
                    <br />
                    {/if}
                    {#if centershiftPreviewSettings?.generic_preview_input?.preprocessing_settings?.sinogram_ring_correction?.do}
                        software ring correction: {centershiftPreviewSettings?.generic_preview_input?.preprocessing_settings
                            ?.sinogram_ring_correction?.do}
                        <br />
                    {/if}
                    {#if multiCentershiftPreviewSettings}
                        {#each multiCentershiftPreviewSettings.centershiftPreviewSettingsArr as settings}
                            delta: {settings?.generic_preview_input?.phase_retrieval_settings?.phase_retrieval?.delta?.toExponential()}
                            &nbsp;&nbsp;/&nbsp;&nbsp;
                            mu: {settings?.generic_preview_input?.phase_retrieval_settings?.phase_retrieval?.mu?.toPrecision(
                                3
                            )}
                            <br />
                        {/each}
                    {:else}
                        delta: {centershiftPreviewSettings?.generic_preview_input?.phase_retrieval_settings?.phase_retrieval?.delta?.toExponential()}
                        <br />
                        mu: {centershiftPreviewSettings?.generic_preview_input?.phase_retrieval_settings?.phase_retrieval?.mu?.toPrecision(
                            3
                        )}
                        <br />
                    {/if}
                    <br />
                </settings>
                <h3>Reconstruction settings</h3>
                <settings>
                    {#if centershiftPreviewSettings?.generic_preview_input?.reconstruction_settings
                        ?.centershift_tolerance_object_mm}
                        centershift tolerance: {centershiftPreviewSettings?.generic_preview_input?.reconstruction_settings
                            ?.centershift_tolerance_object_mm} mm
                        <br />
                    {/if}
                    {#if centershiftPreviewSettings?.generic_preview_input?.reconstruction_settings?.centershift_object_mm}
                        centershift: {centershiftPreviewSettings?.generic_preview_input?.reconstruction_settings
                            ?.centershift_object_mm} mm
                        {#if centershiftPreviewSettings?.generic_preview_input?.reconstruction_settings?.centershift_is_optimized}
                            &emsp;(optimized)
                        {/if}
                        <br />
                    {/if}
                    {#if centershiftPreviewSettings?.centershift_optimization}
                        centershift optimization: {centershiftPreviewSettings?.centershift_optimization}
                        <br />
                    {/if}
                    {#if isNumeric(centershiftPreviewSettings?.generic_preview_input?.reconstruction_settings?.tilt_deg)}
                        tilt: {centershiftPreviewSettings?.generic_preview_input?.reconstruction_settings?.tilt_deg}°
                        {#if centershiftPreviewSettings?.generic_preview_input?.reconstruction_settings?.tilt_is_optimized}
                            &emsp;(optimized)
                        {/if}
                        <br />
                    {/if}
                    slices:
                    {#if multiCentershiftPreviewSettings?.slices}
                        {multiCentershiftPreviewSettings?.slices}&nbsp;
                    {/if}
                </settings>
            {/if}
        </Modal>
    {/if}
</Preview>

<style>
    centershift-advanced {
        display: block;
    }
    .centershift-form {
        display: grid;
        grid-template-columns: 15% 30% 5% 20% 20%;
        grid-column-gap: 2.5%;
        align-items: baseline;
    }

    .centershift-form button {
        margin-left: 2em;
    }

    :global(.icon-and-text-button svg) {
        vertical-align: text-bottom;
        margin-right: 8px;
        margin-top: -4px;
        margin-bottom: -2px;
    }

    :global(#previewsliderCentershift) {
        width: 100%;
        margin: 0;
        margin-left: auto;
        margin-right: auto;
        margin-bottom: 3em;
    }

    @property --refine-color-edge {
        syntax: "<color>";
        initial-value: var(--slider);
        inherits: false;
    }

    @property --refine-color-mid {
        syntax: "<color>";
        initial-value: hsl(219, 73%, 65%);
        inherits: false;
    }

    @property --rangeleft {
        syntax: "<percentage>";
        initial-value: 0%;
        inherits: false;
    }

    @property --rangeright {
        syntax: "<percentage>";
        initial-value: 100%;
        inherits: false;
    }

    @property --pipsleft {
        syntax: "<percentage>";
        initial-value: 0%;
        inherits: true;
    }

    @property --pipsright {
        syntax: "<percentage>";
        initial-value: 100%;
        inherits: true;
    }

    :root {
        --pipsleft: 0%;
        --pipsright: 100%;
    }

    :global(#previewsliderCentershift.rangeSlider) {
        background: linear-gradient(to right, var(--refine-color-edge) 0% var(--rangeleft), var(--refine-color-mid) var(--rangeleft) var(--rangeright), var(--refine-color-edge) var(--rangeright));
        transition: --rangeleft 0.25s, --rangeright 0.25s, --refine-color 0.5s;
    }

    :global(#previewsliderCentershift div.rangePips) {
        left: var(--pipsleft);
        right: var(--pipsright);
        width: calc(var(--pipsright) - var(--pipsleft));
        max-width: 100%;
        transition: --pipsleft 0.25s, --pipsright 0.25s;
    }

    :global(#previewsliderCentershift .pip.last, #previewsliderCentershift .pip.first) {
        height: 0.75em;
    }
    :global(#previewsliderCentershift .rangePips .pip.first .pipVal) {
        padding-left: 3em;
    }
    :global(#previewsliderCentershift .rangePips .pip.last .pipVal) {
        padding-right: 3em;
    }

    settings {
        font-family: monospace;
    }
</style>
